import { Fragment, useState, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import { useMediaFolders, useCreateFolder } from '@/api/hooks/useMediaLibrary'
import { FolderPanel } from './FolderPanel'
import { MediaGrid } from './MediaGrid'
import { MediaPreview } from './MediaPreview'
import { CutsPanel } from './CutsPanel'
import { VideoCutTab } from './VideoCutTab'
import { VideoCutDialog } from '@/pages/Poster/VideoCutDialog'
import { QuickScheduleDialog } from './QuickScheduleDialog'
import { GoogleDriveTab } from './GoogleDriveTab'
import { StorageBar } from '@/components/shared/StorageBar'
import { ImageIcon, Scissors, FolderOpen, Video, Plus, Check, X, ChevronRight, HardDrive, Sparkles } from 'lucide-react'
import { useLanguage } from '@/i18n/LanguageContext'
import { toast } from 'sonner'
import type { MediaFolder, MediaImage } from '@/types'

function buildBreadcrumbs(folders: MediaFolder[], currentId: string | null) {
  const path: Array<{ id: string | null; name: string }> = []
  let id = currentId
  while (id) {
    const folder = folders.find(f => f.id === id)
    if (!folder) break
    path.unshift({ id: folder.id, name: folder.name })
    id = folder.parentId || null
  }
  path.unshift({ id: null, name: 'Raiz' })
  return path
}

export function Media() {
  const { t } = useLanguage()
  const { data: folders } = useMediaFolders()
  const createFolder = useCreateFolder()
  const location = useLocation()

  const [currentFolderId, setCurrentFolderId] = useState<string | null>(null)
  const [previewMedia, setPreviewMedia] = useState<MediaImage | null>(null)
  const [cutVideo, setCutVideo] = useState<MediaImage | null>(null)
  const [showMobileCreate, setShowMobileCreate] = useState(false)
  const [mobileNewName, setMobileNewName] = useState('')
  const [scheduleConfig, setScheduleConfig] = useState<{
    folders: Array<{ id: string; name: string; clipCount?: number }>
    cutMode: boolean
  } | null>(null)

  // Close any open dialog/preview when this page unmounts so Radix can
  // properly clean up scroll-lock and pointer-events on body. Without this,
  // navigating to another route while a dialog is open can leave the UI stuck.
  useEffect(() => {
    return () => {
      setPreviewMedia(null)
      setCutVideo(null)
      setScheduleConfig(null)
    }
  }, [])

  // Same defensive cleanup if pathname changes while we're still mounted
  // (covers edge cases where unmount is delayed by suspense / transitions).
  useEffect(() => {
    setPreviewMedia(null)
    setCutVideo(null)
    setScheduleConfig(null)
  }, [location.pathname])

  // Handle ?tab=drive from OAuth callback + success/error toasts
  const urlParams = new URLSearchParams(window.location.search)
  const defaultTab = urlParams.get('tab') === 'drive' ? 'drive' : 'library'
  const [activeTab, setActiveTab] = useState(defaultTab)

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const success = params.get('success')
    const error = params.get('error')
    if (success === 'drive_connected') {
      const email = params.get('email')
      toast.success(`Google Drive conectado${email ? `: ${email}` : ''}`)
      window.history.replaceState({}, '', window.location.pathname)
    }
    if (error) {
      const msg = params.get('message')
      toast.error(`Erro: ${msg || error}`)
      window.history.replaceState({}, '', window.location.pathname)
    }
  }, [])

  // Filter by type
  const libraryFolders = folders?.filter(f => f.type !== 'cuts') || []
  const cutFolders = folders?.filter(f => f.type === 'cuts') || []

  // Current folder and its children
  const currentFolder = libraryFolders.find(f => f.id === currentFolderId) || null
  const currentSubfolders = libraryFolders.filter(f =>
    currentFolderId ? f.parentId === currentFolderId : false
  )

  // Mobile: current level folders (children of current parent)
  const currentLevelFolders = libraryFolders.filter(f =>
    currentFolderId ? f.parentId === currentFolderId : !f.parentId
  )
  const breadcrumbs = buildBreadcrumbs(libraryFolders, currentFolderId)

  const handleMobileCreate = () => {
    if (!mobileNewName.trim()) return
    createFolder.mutate({ name: mobileNewName.trim(), parentId: currentFolderId }, {
      onSuccess: (data) => {
        toast.success(t('Pasta criada!'))
        setMobileNewName('')
        setShowMobileCreate(false)
        if (data?.id) setCurrentFolderId(data.id)
      },
      onError: (err) => toast.error(err.message),
    })
  }

  return (
    <div className="space-y-5">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-primary/20 to-primary/5">
            <ImageIcon className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h1 className="text-xl font-bold tracking-tight">{t('Midias')}</h1>
            <p className="text-xs text-muted-foreground">{t('Gerencie suas imagens e videos, e corte videos em clips')}</p>
          </div>
        </div>
      </div>

      <StorageBar />

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="flex flex-col" style={{ minHeight: 'calc(100vh - 260px)' }}>
        <div className="mb-4">
          <TabsList className="inline-flex h-11 items-center rounded-2xl bg-muted/60 p-1 gap-1 backdrop-blur-sm">
            <TabsTrigger
              value="library"
              className="rounded-xl px-4 py-2 text-xs font-medium transition-all duration-200 data-[state=active]:bg-card data-[state=active]:text-foreground data-[state=active]:shadow-sm"
            >
              <FolderOpen className="mr-1.5 h-3.5 w-3.5" /> {t('Biblioteca')}
            </TabsTrigger>
            <TabsTrigger
              value="cuts"
              className="rounded-xl px-4 py-2 text-xs font-medium transition-all duration-200 data-[state=active]:bg-card data-[state=active]:text-foreground data-[state=active]:shadow-sm"
            >
              <Video className="mr-1.5 h-3.5 w-3.5" /> {t('Cortes')}
              {cutFolders.length > 0 && (
                <span className="ml-1.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-primary/15 px-1.5 text-[10px] font-bold text-primary">
                  {cutFolders.length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger
              value="cut"
              className="rounded-xl px-4 py-2 text-xs font-medium transition-all duration-200 data-[state=active]:bg-card data-[state=active]:text-foreground data-[state=active]:shadow-sm"
            >
              <Scissors className="mr-1.5 h-3.5 w-3.5" /> {t('Cortar Videos')}
            </TabsTrigger>
            <TabsTrigger
              value="drive"
              className="rounded-xl px-4 py-2 text-xs font-medium transition-all duration-200 data-[state=active]:bg-card data-[state=active]:text-foreground data-[state=active]:shadow-sm"
            >
              <HardDrive className="mr-1.5 h-3.5 w-3.5" /> Google Drive
            </TabsTrigger>
          </TabsList>
        </div>

        {/* Library tab */}
        <TabsContent value="library" className="mt-0 flex flex-1 overflow-hidden rounded-2xl border border-border/40 bg-card/50 shadow-sm">
          {/* Desktop: side panel */}
          <div className="hidden md:block">
            <FolderPanel
              currentFolderId={currentFolderId}
              onNavigate={setCurrentFolderId}
              onScheduleFolders={(folders) => setScheduleConfig({ folders, cutMode: false })}
            />
          </div>

          <div className="flex flex-1 flex-col overflow-hidden">
            {/* Mobile: breadcrumb + folder chips */}
            <div className="flex flex-col gap-2 border-b border-border/40 p-3 md:hidden">
              {showMobileCreate ? (
                <div className="flex items-center gap-2">
                  <Input
                    value={mobileNewName}
                    onChange={(e) => setMobileNewName(e.target.value)}
                    placeholder={t('Nome da pasta...')}
                    className="h-9 flex-1 text-sm rounded-xl"
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleMobileCreate()
                      if (e.key === 'Escape') { setShowMobileCreate(false); setMobileNewName('') }
                    }}
                  />
                  <Button size="icon" variant="ghost" className="h-8 w-8 rounded-lg" onClick={handleMobileCreate} disabled={createFolder.isPending}>
                    <Check className="h-4 w-4 text-primary" />
                  </Button>
                  <Button size="icon" variant="ghost" className="h-8 w-8 rounded-lg" onClick={() => { setShowMobileCreate(false); setMobileNewName('') }}>
                    <X className="h-4 w-4 text-muted-foreground" />
                  </Button>
                </div>
              ) : (
                <>
                  {/* Breadcrumbs */}
                  <div className="flex items-center gap-1 text-xs text-muted-foreground overflow-x-auto no-scrollbar">
                    {breadcrumbs.map((crumb, i) => (
                      <Fragment key={crumb.id ?? 'root'}>
                        {i > 0 && <ChevronRight className="h-3 w-3 shrink-0 text-muted-foreground" />}
                        <button
                          type="button"
                          onClick={() => setCurrentFolderId(crumb.id)}
                          className={cn(
                            'shrink-0 rounded-lg px-1.5 py-0.5 transition-colors',
                            crumb.id === currentFolderId
                              ? 'text-foreground font-semibold'
                              : 'hover:text-foreground hover:bg-muted/50',
                          )}
                        >
                          {crumb.name}
                        </button>
                      </Fragment>
                    ))}
                  </div>
                  {/* Current level folders as scrollable chips */}
                  <div className="flex items-center gap-2 overflow-x-auto no-scrollbar">
                    {currentLevelFolders.map(f => (
                      <button
                        key={f.id}
                        onClick={() => setCurrentFolderId(f.id)}
                        className={cn(
                          'shrink-0 rounded-xl border px-3 py-1.5 text-xs transition-all duration-200',
                          f.id === currentFolderId
                            ? 'border-primary/40 bg-primary/10 text-foreground font-semibold shadow-sm'
                            : 'border-border/40 hover:bg-muted/50 hover:border-border',
                        )}
                      >
                        {f.name} ({f.images.length})
                      </button>
                    ))}
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-8 gap-1 shrink-0 text-xs rounded-xl border-dashed"
                      onClick={() => setShowMobileCreate(true)}
                    >
                      <Plus className="h-3 w-3" /> {t('Nova')}
                    </Button>
                  </div>
                </>
              )}
            </div>

            {/* Content area */}
            {currentFolder ? (
              <MediaGrid
                folder={currentFolder}
                subfolders={currentSubfolders}
                onPreview={setPreviewMedia}
                onCut={setCutVideo}
                onSchedule={() => setScheduleConfig({ folders: [{ id: currentFolder.id, name: currentFolder.name, imageCount: currentFolder.images.length }], cutMode: false })}
                onNavigateToFolder={setCurrentFolderId}
              />
            ) : (
              <div className="flex flex-1 items-center justify-center">
                <div className="flex flex-col items-center gap-4 text-center px-4">
                  <div className="flex h-20 w-20 items-center justify-center rounded-3xl bg-gradient-to-br from-muted/80 to-muted/30">
                    <FolderOpen className="h-10 w-10 text-muted-foreground/40" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">
                      {libraryFolders.length > 0
                        ? t('Selecione uma pasta para ver o conteudo')
                        : t('Crie uma pasta para comecar')
                      }
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {libraryFolders.length > 0
                        ? t('Escolha uma pasta no painel lateral')
                        : t('Organize suas midias em pastas para facilitar o agendamento')
                      }
                    </p>
                  </div>
                  {libraryFolders.length === 0 && (
                    <Button
                      size="sm"
                      className="gap-1.5 rounded-xl"
                      onClick={() => setShowMobileCreate(true)}
                    >
                      <Plus className="h-3.5 w-3.5" /> {t('Criar primeira pasta')}
                    </Button>
                  )}
                </div>
              </div>
            )}
          </div>
        </TabsContent>

        {/* Cuts tab */}
        <TabsContent value="cuts" className="mt-0 flex flex-1 overflow-hidden rounded-2xl border border-border/40 bg-card/50 shadow-sm">
          <CutsPanel
            folders={cutFolders}
            onSchedule={(folders) => setScheduleConfig({ folders, cutMode: true })}
          />
        </TabsContent>

        {/* Video cut tab */}
        <TabsContent value="cut" className="mt-0 flex-1 overflow-y-auto rounded-2xl border border-border/40 bg-card/50 shadow-sm">
          <VideoCutTab />
        </TabsContent>

        {/* Google Drive tab */}
        <TabsContent value="drive" className="mt-0 flex flex-1 overflow-hidden rounded-2xl border border-border/40 bg-card/50 shadow-sm">
          <GoogleDriveTab />
        </TabsContent>
      </Tabs>

      {/* Preview lightbox */}
      <MediaPreview
        media={previewMedia}
        allMedia={currentFolder?.images || []}
        onClose={() => setPreviewMedia(null)}
        onNavigate={setPreviewMedia}
        onDelete={() => {
          setPreviewMedia(null)
        }}
        onCut={(media) => {
          setPreviewMedia(null)
          setCutVideo(media)
        }}
      />

      {/* Video cut dialog (from library grid) */}
      {currentFolder && (
        <VideoCutDialog
          video={cutVideo}
          folderId={currentFolder.id}
          folderName={currentFolder.name}
          onClose={() => setCutVideo(null)}
        />
      )}

      {/* Quick schedule dialog */}
      <QuickScheduleDialog
        open={!!scheduleConfig}
        onOpenChange={(open) => { if (!open) setScheduleConfig(null) }}
        folders={scheduleConfig?.folders || []}
        cutMode={scheduleConfig?.cutMode}
      />
    </div>
  )
}
